import type { AssociatedObject } from "./types";

/**
 * Parse a value from object-search meta_data->> extraction.
 * JSONB arrays/objects come back as text strings and need to be parsed.
 */
function parseFieldValue(value: any): any {
  if (typeof value !== "string") return value;

  // Try to parse JSON arrays and objects
  if (
    (value.startsWith("[") && value.endsWith("]")) ||
    (value.startsWith("{") && value.endsWith("}"))
  ) {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  // Parse booleans
  if (value === "true") return true;
  if (value === "false") return false;

  return value;
}

/**
 * Convert an object-search record into an AssociatedObject-compatible shape.
 *
 * Object-search returns: { id, fields: { subject, sender, recipients, ... } }
 * UI components expect: { id, meta_data: { subject, sender, recipients, ... }, created_at, ... }
 *
 * Since custom objects store data in meta_data JSONB and object-search extracts fields
 * via `meta_data->>'field_name'`, the flat `fields` map IS the meta_data content.
 */
export function parseObjectSearchRecord(
  record: any,
  _objectSlug?: string,
): AssociatedObject {
  const rawFields = record?.fields || {};

  // Parse each field value (restores arrays/objects from JSONB text extraction)
  const parsedFields: Record<string, any> = {};
  for (const [key, value] of Object.entries(rawFields)) {
    parsedFields[key] = parseFieldValue(value);
  }

  return {
    id: record.id || parsedFields.external_id || "",
    external_id: record.id || parsedFields.external_id || "",
    meta_data: parsedFields,
    created_at: parsedFields.created_at || "",
    updated_at: parsedFields.updated_at || "",
    key_fields_text: parsedFields.key_fields_text || "",
    thread_id: parsedFields.thread_id || null,
    company_domain: parsedFields.company_domain,
    object_id: record.object_id || null,
  } as AssociatedObject;
}
